import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { ArrowBack, ExpandMore, Class as ClassIcon, CalendarToday } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const WeeklyReports = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedSubject, setExpandedSubject] = useState(null);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await principalAPI.getClasses();
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekOptions = () => {
    const weeks = [];
    const currentYear = new Date().getFullYear();
    const startOfYear = new Date(currentYear, 0, 1);
    
    for (let i = 1; i <= 52; i++) {
      const weekStartDate = new Date(startOfYear);
      weekStartDate.setDate(startOfYear.getDate() + (i - 1) * 7);
      
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6);
      
      const formatDate = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}`;
      };
      
      weeks.push({
        number: i,
        label: `Week ${i} (${formatDate(weekStartDate)} - ${formatDate(weekEndDate)})`
      });
    }
    return weeks;
  };

  const handleFetchReport = async () => {
    if (!selectedClass || !selectedWeek) return;

    setLoadingReport(true);
    try {
      const response = await principalAPI.getWeeklyReport(selectedClass, selectedWeek);
      setWeeklyReport(response.data);
    } catch (error) {
      console.error('Error fetching weekly report:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleAccordionChange = (subjectId) => (event, isExpanded) => {
    setExpandedSubject(isExpanded ? subjectId : null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Weekly Plans
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/principal/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Selection Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Select Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                label="Select Class"
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name} - Grade {cls.grade} {cls.section}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Select Week</InputLabel>
              <Select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                label="Select Week"
              >
                {getWeekOptions().map((week) => (
                  <MenuItem key={week.number} value={week.number}>
                    {week.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleFetchReport}
              disabled={!selectedClass || !selectedWeek || loadingReport}
            >
              {loadingReport ? <CircularProgress size={24} /> : 'View Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Report Display */}
      {weeklyReport && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Class
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {weeklyReport.class?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Grade {weeklyReport.class?.grade} {weeklyReport.class?.section}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Total Subjects
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {weeklyReport.totalSubjects || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Lesson Plans
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {weeklyReport.completedPlans || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    Teacher
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {weeklyReport.teacher?.name || 'Not Assigned'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Lesson Plans by Subject */}
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Lesson Plans
          </Typography>

          {weeklyReport.plans && weeklyReport.plans.length > 0 ? (
            weeklyReport.plans.map((plan) => (
              <Accordion
                key={plan.id}
                expanded={expandedSubject === plan.id}
                onChange={handleAccordionChange(plan.id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <ClassIcon color="primary" />
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {plan.subject_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Code: {plan.subject_code}
                      </Typography>
                    </Box>
                    <Chip label="Completed" color="success" size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Topic
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {plan.topic}
                        </Typography>
                      </Grid>

                      {plan.learning_objectives && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Learning Objectives
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {plan.learning_objectives}
                          </Typography>
                        </Grid>
                      )}

                      {plan.activities && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Activities
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {plan.activities}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} md={6}>
                        {plan.resources && (
                          <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Resources
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {plan.resources}
                            </Typography>
                          </>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        {plan.homework && (
                          <>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Homework
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {plan.homework}
                            </Typography>
                          </>
                        )}
                      </Grid>

                      {plan.notes && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Additional Notes
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {plan.notes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Created by: {plan.teacher_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(plan.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CalendarToday sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No lesson plans found for this week
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The teacher has not created any lesson plans for Week {selectedWeek} yet.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {!weeklyReport && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CalendarToday sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a class and week to view the report
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default WeeklyReports;
